/**
 * D-EZ-102 — THE RACE, TOLD AS A STORY.
 *
 * Victory Lane, the Big One card, the rain and red-flag moments and the race
 * summary all read the SAME retained `RaceResult` the boards replay. Nothing
 * here rolls, guesses or reorders: every sentence is assembled from a field the
 * engine already wrote, and a fact the result does not hold is left out rather
 * than approximated.
 *
 * Every function is pure. The callers decide WHEN a fact may be shown (a wreck
 * card only once its turn is on screen; Victory Lane only at the flag) — this
 * file only decides what is true.
 */

import type { RaceResult } from '../race.ts';
import type { RuleEvent } from '../ruleEvent.ts';
import type { Car } from '../types.ts';
import { shortTrack, surname } from '../paper/fallbackPaper.ts';

/** A single incident that collects this many cars is the Big One. */
export const BIG_ONE_CARS = 5;

const ORD = ['', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth'];
export function ordinal(n: number): string {
  const v = n % 100;
  const s = v >= 11 && v <= 13 ? 'th' : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
  return `${n}${s}`;
}
const wordOrdinal = (n: number): string => ORD[n] ?? ordinal(n);
const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;
export function listOf(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** Every car that started, by id — names and numbers never come from anywhere else. */
export function carsOf(result: RaceResult): Map<string, Car> {
  const out = new Map<string, Car>();
  for (const c of [...result.state.grid, ...result.state.retired]) out.set(c.card.driverId, c);
  return out;
}

const OUT_WORDS: Record<string, string> = {
  trouble: 'wrecked out',
  problem: 'mechanical',
  feud: 'taken out in a feud',
  fuel: 'out of fuel',
  'black-flag': 'black-flagged',
  'repair-failed': 'could not be repaired',
  injury: 'hurt',
};
export function outWord(reason: string | undefined | null): string {
  return (reason && OUT_WORDS[reason]) ?? 'out';
}

// ---------------------------------------------------------------------------
// The Big One
// ---------------------------------------------------------------------------

export type CarFate = 'out' | 'injured' | 'laps-down' | 'repaired' | 'running';

export interface WreckCar {
  driverId: string;
  name: string;
  carNumber: string;
  fate: CarFate;
  /** Plain words: "out of the race", "repaired, back in the field", "a lap down". */
  fateText: string;
}

export interface BigIncident {
  turn: number;
  tag: string;
  /** Every car collected, in the order the incident lists them. */
  cars: WreckCar[];
  /** Cars that were in the incident's groups and got through. */
  escaped: { driverId: string; name: string }[];
  /** The engine's own sentence for the incident. */
  description: string;
  headline: string;
  /** What the pit reporter says once the dust settles. */
  followUp: string;
  /** Who leads when the field comes back around (the same turn's order). */
  leaderAfter: string | null;
}

type IncidentEvent = RuleEvent & {
  collectedIds?: string[];
  outIds?: string[];
  repairedIds?: string[];
  escapedIds?: string[];
  injuredOutIds?: string[];
};

/**
 * Every incident that collected `min` or more cars in ONE event, oldest first.
 * The fate of each car is read off the incident itself (out, repaired) and off
 * the running order at the end of the same turn (a lap down, or out by a later
 * step of that turn) — never off anything after it.
 */
export function bigIncidents(result: RaceResult, min = BIG_ONE_CARS): BigIncident[] {
  const cars = carsOf(result);
  const name = (id: string) => cars.get(id)?.card.name ?? id;
  const out: BigIncident[] = [];
  for (const turn of result.turns) {
    for (const raw of turn.events) {
      const e = raw as IncidentEvent;
      const collected = e.collectedIds ?? [];
      if (collected.length < min && !(e.momentTag === 'wreck.big-one' && collected.length >= 3)) continue;
      const outIds = new Set(e.outIds ?? []);
      const hurt = new Set(e.injuredOutIds ?? []);
      const repaired = new Set(e.repairedIds ?? []);
      const after = new Map(turn.state.grid.map((c) => [c.card.driverId, c]));
      const retiredAfter = new Map(turn.state.retired.map((c) => [c.card.driverId, c]));
      const list: WreckCar[] = collected.map((id) => {
        const card = cars.get(id)?.card;
        let fate: CarFate;
        let fateText: string;
        if (hurt.has(id) || retiredAfter.get(id)?.outReason === 'injury') {
          fate = 'injured';
          fateText = 'hurt — out of the race';
        } else if (outIds.has(id) || retiredAfter.has(id)) {
          fate = 'out';
          fateText = 'out of the race';
        } else if (after.get(id)?.lapsDown) {
          fate = 'laps-down';
          fateText = repaired.has(id) ? 'repaired, but off the lead lap' : 'off the lead lap';
        } else if (repaired.has(id)) {
          fate = 'repaired';
          fateText = 'repaired, back in the field';
        } else {
          fate = 'running';
          fateText = 'still running';
        }
        return { driverId: id, name: card?.name ?? id, carNumber: card?.carNumber ?? '', fate, fateText };
      });
      const escaped = (e.escapedIds ?? []).filter((id) => !collected.includes(id)).map((id) => ({ driverId: id, name: name(id) }));
      const lost = list.filter((c) => c.fate === 'out' || c.fate === 'injured').length;
      const leader = turn.state.grid[0]?.card.name ?? null;
      const headline =
        e.momentTag === 'wreck.big-one' || collected.length >= 8
          ? `The Big One — ${plural(collected.length, 'car')} collected`
          : `A ${collected.length}-car wreck`;
      out.push({
        turn: turn.turn,
        tag: e.momentTag,
        cars: list,
        escaped,
        description: e.description,
        headline,
        followUp: followUpLine(escaped.map((x) => x.name), lost, list.length, leader),
        leaderAfter: leader,
      });
    }
  }
  return out;
}

/** The pit reporter's line once the field has come back around. */
function followUpLine(escapedNames: string[], lost: number, collected: number, leader: string | null): string {
  const parts: string[] = [];
  if (escapedNames.length > 0) {
    const shown = escapedNames.slice(0, 3);
    const more = escapedNames.length - shown.length;
    const who = more > 0 ? `${shown.join(', ')} and ${plural(more, 'other')}` : listOf(shown);
    parts.push(`${who} ${escapedNames.length === 1 ? 'threads' : 'thread'} through untouched.`);
  } else {
    parts.push('Nobody in that pack gets through clean.');
  }
  parts.push(
    lost === 0
      ? `All ${collected} cars caught up in it keep going.`
      : lost === collected
        ? `Every one of the ${collected} is done for the day.`
        : `${plural(lost, 'car')} of the ${collected} ${lost === 1 ? 'is' : 'are'} done for the day.`,
  );
  if (leader) parts.push(`${leader} leads when they come back around.`);
  return parts.join(' ');
}

// ---------------------------------------------------------------------------
// The other moments a broadcast stops for
// ---------------------------------------------------------------------------

export type RaceMomentKind = 'big-one' | 'rain-threat' | 'rain-delay' | 'rain-shortened' | 'red-flag' | 'cancelled';

export interface RaceMoment {
  turn: number;
  kind: RaceMomentKind;
  title: string;
  lines: string[];
  incident?: BigIncident;
}

/**
 * Every moment worth stopping the broadcast for, oldest first: the Big One,
 * a red flag, rain on the way, a rain delay, a race ended by rain or called off.
 */
export function raceMoments(result: RaceResult): RaceMoment[] {
  const out: RaceMoment[] = [];
  for (const incident of bigIncidents(result)) {
    out.push({ turn: incident.turn, kind: 'big-one', title: incident.headline, lines: [incident.description], incident });
  }
  const seen = new Set<string>();
  for (const e of result.events) {
    const key = `${e.turn}|${e.momentTag}`;
    if (seen.has(key)) continue;
    const at = `Turn ${e.turn} of ${result.scheduledTurns}`;
    switch (e.momentTag) {
      case 'rare.rain-threat.set':
        out.push({ turn: e.turn, kind: 'rain-threat', title: 'Rain in the area', lines: [e.description] });
        break;
      case 'rain.delay':
        out.push({
          turn: e.turn,
          kind: 'rain-delay',
          title: 'Rain delay',
          lines: [e.description, `${at}. Not yet half distance, so the race is not official — they will wait it out.`],
        });
        break;
      case 'race.rain-shortened':
        out.push({
          turn: e.turn,
          kind: 'rain-shortened',
          title: 'Rain ends it',
          lines: [e.description, `${at} complete. Past half distance, the race is official as it stands.`],
        });
        break;
      case 'rare.red-flag':
        out.push({ turn: e.turn, kind: 'red-flag', title: 'Red flag', lines: [e.description] });
        break;
      case 'race.cancelled':
        out.push({ turn: e.turn, kind: 'cancelled', title: 'The race is called off', lines: [e.description] });
        break;
      default:
        continue;
    }
    seen.add(key);
  }
  return out.sort((a, b) => a.turn - b.turn);
}

/** The turns a running broadcast should hold on, so the moment can be read. */
export function holdTurns(result: RaceResult): Set<number> {
  return new Set(raceMoments(result).filter((m) => m.kind !== 'rain-threat').map((m) => m.turn));
}

/**
 * Turns that deserve the big picture in the TV cutaway: a lead change in the
 * last three turns, every moment above, and the last turn of the race.
 */
export function bigTurns(result: RaceResult): Set<number> {
  const out = holdTurns(result);
  for (const c of result.leadChanges) if (result.scheduledTurns - c.turn <= 3) out.add(c.turn);
  if (result.turnsRun > 0) out.add(result.turnsRun);
  return out;
}

/**
 * The moment on screen at `cursor`: the latest one at or before the turn shown,
 * held for `hold` turns after it. Never a moment whose turn is not yet shown.
 */
export function momentAt(moments: readonly RaceMoment[], cursor: number, hold = 2): RaceMoment | null {
  let found: RaceMoment | null = null;
  for (const m of moments) if (m.turn <= cursor && cursor - m.turn <= hold) found = m;
  return found;
}

// ---------------------------------------------------------------------------
// How it was won
// ---------------------------------------------------------------------------

export type WinKind =
  | 'rain-shortened'
  | 'called'
  | 'photo-finish'
  | 'last-turn-pass'
  | 'late-pass'
  | 'flag-to-flag'
  | 'led-most'
  | 'won';

export interface HowWon {
  kind: WinKind;
  /** One plain sentence. */
  text: string;
}

export function howItWasWon(result: RaceResult): HowWon | null {
  const winnerId = result.winnerId;
  if (!winnerId) return null;
  const winner = result.finishingOrder.find((f) => f.driverId === winnerId);
  if (!winner) return null;
  if (result.called) {
    return result.called.reason === 'rain'
      ? { kind: 'rain-shortened', text: `Rain-shortened at turn ${result.turnsRun} of ${result.scheduledTurns} — out front when the rain came.` }
      : { kind: 'called', text: `The race was called after turn ${result.turnsRun} of ${result.scheduledTurns} — out front when it stopped.` };
  }
  if (result.photoFinish?.leadChanged) {
    return { kind: 'photo-finish', text: 'Won it at the line in a photo finish.' };
  }
  const changes = result.leadChanges;
  const last = changes[changes.length - 1];
  if (last && last.driverId === winnerId && last.turn >= result.turnsRun) {
    return { kind: 'last-turn-pass', text: 'Took the lead on the final turn.' };
  }
  if (last && last.driverId === winnerId && result.turnsRun - last.turn <= 3) {
    const left = result.turnsRun - last.turn;
    return { kind: 'late-pass', text: `Took the lead with ${plural(left, 'turn')} to go and held on.` };
  }
  if (winner.turnsLed >= result.turnsRun && result.turnsRun > 0) {
    return { kind: 'flag-to-flag', text: `Led every one of the ${result.turnsRun} turns.` };
  }
  const most = Math.max(0, ...result.finishingOrder.map((f) => f.turnsLed));
  if (winner.turnsLed > 0 && winner.turnsLed === most) {
    return { kind: 'led-most', text: `Led the most — ${winner.turnsLed} of ${result.turnsRun} turns.` };
  }
  return {
    kind: 'won',
    text: winner.turnsLed > 0 ? `Led ${plural(winner.turnsLed, 'turn')}, including the one that counted.` : 'Out front when it counted.',
  };
}

// ---------------------------------------------------------------------------
// The paragraph at the top of the results
// ---------------------------------------------------------------------------

/**
 * One paragraph, from facts: the winner and how, the cautions (the Big One by
 * name), the lead changes, and the notable cars that did not finish.
 */
export function raceStoryParagraph(result: RaceResult): string {
  const cars = carsOf(result);
  const name = (id: string) => cars.get(id)?.card.name ?? result.finishingOrder.find((f) => f.driverId === id)?.name ?? id;
  const winner = result.finishingOrder.find((f) => f.driverId === result.winnerId);
  const parts: string[] = [];
  if (winner) {
    const start = result.startingOrder ? result.startingOrder.indexOf(winner.driverId) + 1 : 0;
    const how = howItWasWon(result);
    parts.push(
      `${winner.name} won the ${result.state.raceName}${start > 0 ? ` from ${ordinal(start)} on the grid` : ''}.${how ? ` ${how.text}` : ''}`,
    );
  } else {
    parts.push(`Nobody was running at the end of the ${result.state.raceName}.`);
  }
  if (result.called?.reason === 'rain') parts.push(`It was rain-shortened at turn ${result.turnsRun} of ${result.scheduledTurns}.`);
  else if (result.called) parts.push(`It was called after turn ${result.turnsRun} of ${result.scheduledTurns}.`);

  const wrecks = bigIncidents(result);
  const cautions = result.cautions.length;
  if (cautions === 0) parts.push('It ran green from start to finish.');
  else {
    const biggest = wrecks.reduce<BigIncident | null>((b, w) => (!b || w.cars.length > b.cars.length ? w : b), null);
    parts.push(
      `There ${cautions === 1 ? 'was one caution' : `were ${cautions} cautions`}${
        biggest ? `, the worst of them a ${biggest.cars.length}-car wreck on turn ${biggest.turn}` : ''
      }.`,
    );
  }
  const leaders = new Set(result.leadChanges.map((c) => c.driverId));
  if (result.leadChanges.length > 0) {
    parts.push(`The lead changed hands ${plural(result.leadChanges.length, 'time')} among ${plural(leaders.size, 'driver')}.`);
  }
  // Notable cars out: anyone who led, or who started in the top ten.
  const startOf = (id: string) => (result.startingOrder ? result.startingOrder.indexOf(id) + 1 : 0);
  const notable = result.finishingOrder
    .filter((f) => f.status === 'out' && (f.turnsLed > 0 || (startOf(f.driverId) > 0 && startOf(f.driverId) <= 10)))
    .sort((a, b) => b.turnsLed - a.turnsLed || (a.outTurn ?? 0) - (b.outTurn ?? 0))
    .slice(0, 3);
  if (notable.length > 0) {
    parts.push(
      `Among those who did not finish: ${listOf(
        notable.map((f) => `${name(f.driverId)} (${outWord(f.outReason)}, turn ${f.outTurn ?? '?'})`),
      )}.`,
    );
  }
  return parts.join(' ');
}

// ---------------------------------------------------------------------------
// The headline Victory Lane runs under
// ---------------------------------------------------------------------------

export interface HeadlineFacts {
  winnerName: string;
  raceName: string;
  trackName: string;
  how: HowWon | null;
  started: number | null;
  firstCareerWin: boolean;
  /** A round-number career win reached today (10th, 25th…), or null. */
  winMilestone: number | null;
  bigOne: boolean;
  defendedRace: boolean;
}

/**
 * The game's own headline, from facts and nothing else, ranked the way a
 * racing weekly would run it: a first win beats a photo finish beats the rain.
 */
export function victoryHeadline(f: HeadlineFacts): string {
  const who = surname(f.winnerName);
  const track = shortTrack(f.trackName);
  if (f.firstCareerWin) return `${who} breaks through at ${track}`;
  if (f.how?.kind === 'photo-finish') return `${who} by a nose at ${track}`;
  if (f.how?.kind === 'rain-shortened') return `${who} wins as the rain falls at ${track}`;
  if (f.how?.kind === 'called') return `${who} out front when ${track} is called`;
  if (f.how?.kind === 'last-turn-pass') return `${who} steals it on the last turn at ${track}`;
  if (f.winMilestone) return `${who} reaches ${f.winMilestone} career wins at ${track}`;
  if (f.defendedRace) return `${who} does it again in the ${f.raceName}`;
  if (f.started !== null && f.started >= 20) return `${who} charges from ${ordinal(f.started)} to win at ${track}`;
  if (f.bigOne) return `${who} survives the Big One to win at ${track}`;
  if (f.how?.kind === 'flag-to-flag') return `${who} leads them all the way at ${track}`;
  return `${who} wins the ${f.raceName}`;
}

/** The car's starting spot, 1-based, or null when the result did not keep the grid. */
export function startOf(result: RaceResult, driverId: string): number | null {
  const i = result.startingOrder?.indexOf(driverId) ?? -1;
  return i >= 0 ? i + 1 : null;
}

export { wordOrdinal, plural };
